import { withDefaults } from "stores/utils/withDefaults";
import { createStore } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";

import { SelectedWellT } from "components/well-list/context/WellListContext";

// ─── Constants ───────────────────────────────────────────────────────────────
export const MAX_PREVIOUSLY_SELECTED_WELLS = 10;
export const DEFAULT_UWID_FORMAT = "entityName";

// ─── State ───────────────────────────────────────────────────────────────────
// Client-selection slice of the replaced `TypeLogContext` — `document`/`wells`
// are backend-sourced and live in React Query (`useTypeLogDocumentQuery.ts`,
// `useTypeLogWellsQuery.ts`) per STATE_OWNERSHIP_CONTRACT.md §3.1/§3.2.
// `typeLogErrorMessage` is dropped entirely — derived at the call site from
// query error state (Standing Rule 6 — derive, don't duplicate).
export interface TypeLogState {
  selectedWell?: SelectedWellT;
  previouslySelectedWells: SelectedWellT[]; // MRU-10, most-recent-first
  uwidFormat: string;
}

// ─── Actions ─────────────────────────────────────────────────────────────────
export interface TypeLogActions {
  // Single atomic action: reads the current `selectedWell` via get(), pushes
  // it onto the MRU-10 history (dedup by Uwi, most-recent-first, capped at
  // 10), then sets the new `selectedWell` — all in one state transition. This
  // reproduces the old TypeLogContext reducer's combined "set previously
  // selected wells" case exactly and avoids the stale-closure bug two
  // separate setSelectedWell()/setPreviouslySelectedWells() calls would risk.
  readonly selectWell: (next: SelectedWellT) => void;
  readonly setUwidFormat: (format: string) => void;
  readonly reset: () => void;
}

// ─── Combined store type ─────────────────────────────────────────────────────
export type TypeLogStore = TypeLogState & TypeLogActions;

// ─── Initial state factory ───────────────────────────────────────────────────
const makeTypeLogDefaults = (): TypeLogState => ({
  selectedWell: undefined,
  previouslySelectedWells: [],
  uwidFormat: DEFAULT_UWID_FORMAT
});

export const createInitialState = withDefaults(makeTypeLogDefaults);

// ─── Store factory ───────────────────────────────────────────────────────────
export const createTypeLogStore = (overrides?: Partial<TypeLogState>) => {
  const initial = createInitialState(overrides);

  return createStore<TypeLogStore>()(
    subscribeWithSelector((set, get) => ({
      ...initial,

      selectWell: (next) => {
        const current = get().selectedWell;
        let previouslySelectedWells = get().previouslySelectedWells;

        if (current) {
          previouslySelectedWells = [
            current,
            ...previouslySelectedWells.filter((well) => well.Uwi !== current.Uwi)
          ].slice(0, MAX_PREVIOUSLY_SELECTED_WELLS);
        }

        set({ selectedWell: next, previouslySelectedWells });
      },

      setUwidFormat: (uwidFormat) => set({ uwidFormat }),

      reset: () => set(createInitialState(overrides))
    }))
  );
};

export type TypeLogStoreApi = ReturnType<typeof createTypeLogStore>;
